import type { Session } from 'types/Session'

import GA4 from 'helpers/ga4'
import Amplitude from 'helpers/amplitude'
import KinesisAnalytics from 'helpers/kinesisAnalytics'
import Brand from 'helpers/brand'

export type AnalyticsProvider = 'ga4' | 'kinesis' | 'amplitude'

export type AnalyticsEvent = {
  category: string
  action: string
  label?: string
  value?: string
  // Extra event context. GA4 only accepts its fixed category/action/label/value shape,
  // so these reach Kinesis and Amplitude only.
  properties?: Record<string, unknown>
  // Narrows the fan out for events that only belong in one pipeline.
  only?: AnalyticsProvider[]
}

const ALL_PROVIDERS: AnalyticsProvider[] = ['ga4', 'kinesis', 'amplitude']

// The single entry point for product analytics. Provider specific configuration stays
// in the provider helpers (environment ids and keys, the gtag script), so callers only
// describe the event.
class Analytics {
  static skipAnalytics = false

  // Admin traffic is left out of every pipeline. GA4 keeps its own copy of the opt out,
  // and the event stream providers need the same answer both for #track and for the
  // session and page view tracking they configure at init.
  static skipUser(skip: boolean) {
    Analytics.skipAnalytics = skip

    GA4.skipGoogleAnalytics(skip)
  }

  // Product analytics only covers residential dealers, so commercial brands and admins
  // are never tracked.
  static isTracking() {
    return Brand.isResidential() && !Analytics.skipAnalytics
  }

  // Session and page view auto tracking for the event stream providers. GA4 loads its
  // own script and tracks page views on its own, so it only needs #identify.
  static init(session: Session, brand: string) {
    Analytics.skipUser(session.isAdmin)

    if (!Analytics.isTracking()) return

    Amplitude.init(session, brand)
    KinesisAnalytics.init(session, brand)
  }

  // Amplitude and Kinesis stamp the dealer context onto every event, so only GA4 needs
  // an explicit identify call. Kept separate from #init because it has to run before
  // the first event of a session is tracked for the admin opt out to take effect.
  static identify(session: Session) {
    Analytics.skipUser(session.isAdmin)

    GA4.setUserProperties(session.userId, session.roles, session.dealerId)
  }

  static track({ category, action, label, value, properties, only = ALL_PROVIDERS }: AnalyticsEvent) {
    // GA4 applies the same opt outs on its own, and reports a missing gtag script.
    if (only.includes('ga4')) {
      GA4.sendEvent({ category, action, label, value })
    }

    if (!Analytics.isTracking()) return

    // The action names the event for the event stream providers, which carry the rest
    // of the GA4 shape as properties so the pipelines can be joined on it.
    const eventProperties = {
      category,
      ...(label === undefined ? {} : { label }),
      ...(value === undefined ? {} : { value }),
      ...properties
    }

    if (only.includes('kinesis')) {
      KinesisAnalytics.record(action, eventProperties)
    }

    if (only.includes('amplitude')) {
      Amplitude.track(action, eventProperties)
    }
  }
}

export default Analytics
